import { useTranslation } from 'react-i18next';
import { useSettingsFormStore } from '../store';

const SAMPLERS = [
  'euler', 'euler_ancestral', 'heun', 'dpm_2', 'dpm_2_ancestral',
  'lms', 'dpmpp_2s_ancestral', 'dpmpp_sde', 'dpmpp_2m', 'dpmpp_2m_sde',
  'dpmpp_3m_sde', 'ddpm', 'lcm', 'ddim', 'uni_pc', 'uni_pc_bh2',
] as const;

const SCHEDULERS = [
  'normal', 'karras', 'exponential', 'sgm_uniform', 'simple', 'ddim_uniform', 'beta',
] as const;

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step,
}: {
  readonly label: string;
  readonly value: number;
  readonly onChange: (v: number) => void;
  readonly min: number;
  readonly max: number;
  readonly step: number;
}) {
  return (
    <label className="flex items-center justify-between gap-4">
      <span className="text-sm text-tg-text shrink-0">
        {label}
      </span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const parsed = Number(e.target.value);
          if (!Number.isNaN(parsed)) onChange(parsed);
        }}
        className="w-24 rounded-lg border border-tg-hint/20 bg-tg-secondary px-3 py-2 text-right text-sm text-tg-text transition-all duration-150 hover:border-tg-hint/40 focus:border-[var(--tg-theme-button-color)] focus:outline-none"
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly options: readonly string[];
}) {
  return (
    <label className="flex items-center justify-between gap-4">
      <span className="text-sm text-tg-text shrink-0">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-tg-hint/20 bg-tg-secondary px-3 py-2 text-sm text-tg-text transition-all duration-150 hover:border-tg-hint/40 focus:border-[var(--tg-theme-button-color)] focus:outline-none"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

function ToggleField({
  label,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-tg-text shrink-0">
        {label}
      </span>
      <button
        type="button"
        onClick={() => { onChange(!checked) }}
        className={`relative h-6 w-11 rounded-full transition-colors duration-200 ${
          checked ? 'bg-tg-button-color' : 'bg-tg-hint/30'
        }`}
      >
        <div
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}

export function GenerationDefaultsForm() {
  const { t } = useTranslation();
  const store = useSettingsFormStore();

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-medium text-tg-hint">
        {t('settings.generationDefaults')}
      </h3>

      <div className="rounded-xl border border-tg-hint/10 bg-tg-secondary p-4 shadow-sm flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-tg-text">
            {t('settings.defaultCheckpoint')}
          </span>
          <input
            type="text"
            value={store.defaultCheckpoint ?? ''}
            onChange={(e) =>
              store.setDefaultCheckpoint(e.target.value || null)
            }
            placeholder={t('settings.defaultCheckpointPlaceholder')}
            className="w-full rounded-lg border border-tg-hint/20 bg-tg-bg px-3 py-2 text-sm text-tg-text transition-all duration-150 hover:border-tg-hint/40 focus:border-[var(--tg-theme-button-color)] focus:outline-none"
          />
        </label>

        <SelectField
          label={t('settings.defaultSampler')}
          value={store.defaultSampler}
          onChange={store.setDefaultSampler}
          options={SAMPLERS}
        />

        <SelectField
          label={t('settings.defaultScheduler')}
          value={store.defaultScheduler}
          onChange={store.setDefaultScheduler}
          options={SCHEDULERS}
        />

        <NumberField
          label={t('settings.defaultSteps')}
          value={store.defaultSteps}
          onChange={store.setDefaultSteps}
          min={1}
          max={150}
          step={1}
        />

        <NumberField
          label={t('settings.defaultCfgScale')}
          value={store.defaultCfgScale}
          onChange={store.setDefaultCfgScale}
          min={0}
          max={30}
          step={0.5}
        />

        <NumberField
          label={t('settings.defaultWidth')}
          value={store.defaultWidth}
          onChange={store.setDefaultWidth}
          min={64}
          max={2048}
          step={8}
        />

        <NumberField
          label={t('settings.defaultHeight')}
          value={store.defaultHeight}
          onChange={store.setDefaultHeight}
          min={64}
          max={2048}
          step={8}
        />

        <label className="flex flex-col gap-1">
          <span className="text-sm text-tg-text">
            {t('settings.defaultNegativePrompt')}
          </span>
          <textarea
            value={store.defaultNegativePrompt}
            onChange={(e) => store.setDefaultNegativePrompt(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-tg-hint/20 bg-tg-bg px-3 py-2 text-sm text-tg-text resize-none transition-all duration-150 hover:border-tg-hint/40 focus:border-[var(--tg-theme-button-color)] focus:outline-none"
          />
        </label>

        {/* PAG Section */}
        <div className="flex flex-col gap-3 pt-2 border-t border-tg-hint/10">
          <h4 className="text-xs font-medium text-tg-hint">
            {t('settings.defaultPag')}
          </h4>
          <ToggleField
            label={t('settings.defaultPagEnabled')}
            checked={store.defaultPagEnabled}
            onChange={store.setDefaultPagEnabled}
          />
          {store.defaultPagEnabled && (
            <NumberField
              label={t('settings.defaultPagScale')}
              value={store.defaultPagScale}
              onChange={store.setDefaultPagScale}
              min={0}
              max={10}
              step={0.1}
            />
          )}
        </div>

        {/* FreeU Section */}
        <div className="flex flex-col gap-3 pt-2 border-t border-tg-hint/10">
          <h4 className="text-xs font-medium text-tg-hint">
            {t('settings.defaultFreeU')}
          </h4>
          <ToggleField
            label={t('settings.defaultFreeUEnabled')}
            checked={store.defaultFreeUEnabled}
            onChange={store.setDefaultFreeUEnabled}
          />
          {store.defaultFreeUEnabled && (
            <div className="grid grid-cols-2 gap-3">
              <NumberField
                label={t('settings.defaultFreeUB1')}
                value={store.defaultFreeUB1}
                onChange={store.setDefaultFreeUB1}
                min={0}
                max={3}
                step={0.05}
              />
              <NumberField
                label={t('settings.defaultFreeUB2')}
                value={store.defaultFreeUB2}
                onChange={store.setDefaultFreeUB2}
                min={0}
                max={3}
                step={0.05}
              />
              <NumberField
                label={t('settings.defaultFreeUS1')}
                value={store.defaultFreeUS1}
                onChange={store.setDefaultFreeUS1}
                min={0}
                max={3}
                step={0.05}
              />
              <NumberField
                label={t('settings.defaultFreeUS2')}
                value={store.defaultFreeUS2}
                onChange={store.setDefaultFreeUS2}
                min={0}
                max={3}
                step={0.05}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
